import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_CUSTOMER_DISPLAY, LOYALTY_SIGNUP_DEFAULTS, MAX_BANNER_IMAGE_URLS, normalizeBannerImageUrls, type CustomerDisplaySettings } from '@cafe-pos/shared';
import { supabase } from '../supabase.js';
import { buildLoyaltyJoinUrl } from '../loyaltyJoin.js';
import { uploadCustomerDisplayBanner } from './customerDisplayBanner.js';

interface SettingsRow {
  enabled: boolean;
  banner_title: string;
  banner_message: string;
  banner_image_url: string | null;
  banner_image_urls: string[] | null;
  tip_enabled: boolean;
  tip_preset_1_pct: number;
  tip_preset_2_pct: number;
  tip_preset_3_pct: number;
  tip_small_order_threshold_amd: number;
  tip_small_preset_1_amd: number;
  tip_small_preset_2_amd: number;
  tip_small_preset_3_amd: number;
  tip_timeout_seconds: number;
  tip_department_id: number | null;
  tip_adg_code: string | null;
  tip_product_code: string;
  tip_product_name: string;
  loyalty_signup_enabled: boolean;
  loyalty_signup_url: string | null;
  loyalty_signup_headline: string;
  loyalty_signup_subtext: string;
  loyalty_signup_cta_label: string;
}

interface JoinLinkRow {
  name: string;
  slug: string;
}

function bannerUrlsForForm(urls: string[]): string[] {
  const padded = [...urls];
  while (padded.length < MAX_BANNER_IMAGE_URLS) padded.push('');
  return padded.slice(0, MAX_BANNER_IMAGE_URLS);
}

function rowToForm(data: SettingsRow): CustomerDisplaySettings {
  return {
    enabled: data.enabled ?? DEFAULT_CUSTOMER_DISPLAY.enabled,
    bannerTitle: data.banner_title ?? '',
    bannerMessage: data.banner_message ?? DEFAULT_CUSTOMER_DISPLAY.bannerMessage,
    bannerImageUrls: bannerUrlsForForm(
      normalizeBannerImageUrls(data.banner_image_urls, data.banner_image_url),
    ),
    tipEnabled: data.tip_enabled ?? true,
    tipPreset1Pct: Number(data.tip_preset_1_pct ?? 5),
    tipPreset2Pct: Number(data.tip_preset_2_pct ?? 10),
    tipPreset3Pct: Number(data.tip_preset_3_pct ?? 15),
    tipSmallOrderThresholdAmd: Number(data.tip_small_order_threshold_amd ?? 2000),
    tipSmallPreset1Amd: Number(data.tip_small_preset_1_amd ?? 100),
    tipSmallPreset2Amd: Number(data.tip_small_preset_2_amd ?? 200),
    tipSmallPreset3Amd: Number(data.tip_small_preset_3_amd ?? 500),
    tipTimeoutSeconds: data.tip_timeout_seconds ?? 60,
    tipDepartmentId: data.tip_department_id,
    tipAdgCode: data.tip_adg_code,
    tipProductCode: data.tip_product_code ?? 'TIP',
    tipProductName: data.tip_product_name ?? 'Tip',
    loyaltySignupEnabled: data.loyalty_signup_enabled ?? false,
    loyaltySignupUrl: data.loyalty_signup_url?.trim() ?? '',
    loyaltySignupHeadline: data.loyalty_signup_headline?.trim() || LOYALTY_SIGNUP_DEFAULTS.headline,
    loyaltySignupSubtext: data.loyalty_signup_subtext?.trim() || LOYALTY_SIGNUP_DEFAULTS.subtext,
    loyaltySignupCtaLabel: data.loyalty_signup_cta_label?.trim() || LOYALTY_SIGNUP_DEFAULTS.ctaLabel,
  };
}

function formToRow(clientId: string, form: CustomerDisplaySettings) {
  return {
    client_id: clientId,
    enabled: form.enabled,
    banner_title: form.bannerTitle,
    banner_message: form.bannerMessage,
    banner_image_url: form.bannerImageUrls[0] ?? null,
    banner_image_urls: normalizeBannerImageUrls(form.bannerImageUrls),
    tip_enabled: form.tipEnabled,
    tip_preset_1_pct: form.tipPreset1Pct,
    tip_preset_2_pct: form.tipPreset2Pct,
    tip_preset_3_pct: form.tipPreset3Pct,
    tip_small_order_threshold_amd: form.tipSmallOrderThresholdAmd,
    tip_small_preset_1_amd: form.tipSmallPreset1Amd,
    tip_small_preset_2_amd: form.tipSmallPreset2Amd,
    tip_small_preset_3_amd: form.tipSmallPreset3Amd,
    tip_timeout_seconds: form.tipTimeoutSeconds,
    tip_department_id: form.tipDepartmentId,
    tip_adg_code: form.tipAdgCode,
    tip_product_code: form.tipProductCode,
    tip_product_name: form.tipProductName,
    loyalty_signup_enabled: form.loyaltySignupEnabled && !!form.loyaltySignupUrl.trim(),
    loyalty_signup_url: form.loyaltySignupUrl.trim() || null,
    loyalty_signup_headline: form.loyaltySignupHeadline.trim(),
    loyalty_signup_subtext: form.loyaltySignupSubtext.trim(),
    loyalty_signup_cta_label: form.loyaltySignupCtaLabel.trim(),
    updated_at: new Date().toISOString(),
  };
}

export function CustomerDisplayManager({ clientId }: { clientId: string | null }) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CustomerDisplaySettings>(DEFAULT_CUSTOMER_DISPLAY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingSlot, setUploadingSlot] = useState<number | null>(null);
  const [joinLinks, setJoinLinks] = useState<JoinLinkRow[]>([]);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!supabase || !clientId) {
      setLoading(false);
      return;
    }
    void (async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error: err } = (await (supabase as any)
        .from('customer_display_settings')
        .select('*')
        .eq('client_id', clientId)
        .maybeSingle()) as { data: SettingsRow | null; error: { message: string } | null };
      if (err) setError(err.message);
      else if (data) setForm(rowToForm(data));
      setLoading(false);
    })();
  }, [clientId]);

  useEffect(() => {
    if (!supabase || !clientId) {
      setJoinLinks([]);
      return;
    }
    void (async () => {
      const { data } = await supabase
        .from('loyalty_join_links')
        .select('name, slug')
        .eq('client_id', clientId)
        .eq('active', true)
        .order('name');
      setJoinLinks((data ?? []) as JoinLinkRow[]);
    })();
  }, [clientId]);

  async function persistForm(nextForm: CustomerDisplaySettings): Promise<boolean> {
    if (!supabase || !clientId) return false;
    setError(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: err } = (await (supabase as any)
      .from('customer_display_settings')
      .upsert(formToRow(clientId, nextForm), { onConflict: 'client_id' })) as {
      error: { message: string } | null;
    };
    if (err) {
      setError(err.message);
      return false;
    }
    setSaved(true);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaved(false), 3000);
    return true;
  }

  async function uploadBanner(slot: number, file: File) {
    if (!supabase || !clientId) return;
    setUploadingSlot(slot);
    setError(null);
    try {
      const publicUrl = await uploadCustomerDisplayBanner(supabase, clientId, slot, file);
      const next = bannerUrlsForForm(form.bannerImageUrls);
      next[slot] = publicUrl;
      const nextForm = { ...form, bannerImageUrls: next };
      setForm(nextForm);
      await persistForm(nextForm);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploadingSlot(null);
    }
  }

  async function clearBanner(slot: number) {
    const next = bannerUrlsForForm(form.bannerImageUrls);
    next[slot] = '';
    const nextForm = { ...form, bannerImageUrls: next };
    setForm(nextForm);
    await persistForm(nextForm);
  }

  async function save() {
    if (!supabase || !clientId || saving) return;
    if (form.loyaltySignupEnabled && !form.loyaltySignupUrl.trim()) {
      setError(t('customerDisplay.loyaltyUrlRequired'));
      return;
    }
    setSaving(true);
    await persistForm(form);
    setSaving(false);
  }

  function numField(
    key: keyof CustomerDisplaySettings,
    label: string,
    hint?: string,
  ) {
    return (
      <label style={{ display: 'block', marginBottom: '1rem' }}>
        <span style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>{label}</span>
        {hint && (
          <span className="muted" style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>
            {hint}
          </span>
        )}
        <input
          type="number"
          value={form[key] as number}
          onChange={(e) => setForm({ ...form, [key]: Number(e.target.value) })}
          style={{ width: '100%' }}
        />
      </label>
    );
  }

  if (loading) return <p className="muted">{t('common.loading')}</p>;

  return (
    <div style={{ maxWidth: 720, padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{t('customerDisplay.title')}</h2>
        <p className="muted" style={{ margin: '4px 0 0' }}>
          {t('customerDisplay.subtitle')}
        </p>
      </div>

      {error && (
        <p style={{ color: 'var(--danger)', marginBottom: '1rem' }}>{error}</p>
      )}

      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '1.5rem',
          marginBottom: '1.5rem',
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.25rem' }}>
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          />
          <span style={{ fontWeight: 600 }}>{t('customerDisplay.enabled')}</span>
        </label>
        <p className="muted" style={{ margin: '0 0 1.25rem', fontSize: 13 }}>
          {t('customerDisplay.enabledHint')}
        </p>

        <label style={{ display: 'block', marginBottom: '1rem' }}>
          <span style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
            {t('customerDisplay.bannerTitle')}
          </span>
          <input
            type="text"
            value={form.bannerTitle}
            onChange={(e) => setForm({ ...form, bannerTitle: e.target.value })}
            style={{ width: '100%' }}
          />
        </label>

        <label style={{ display: 'block', marginBottom: '1rem' }}>
          <span style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
            {t('customerDisplay.bannerMessage')}
          </span>
          <textarea
            value={form.bannerMessage}
            onChange={(e) => setForm({ ...form, bannerMessage: e.target.value })}
            rows={2}
            style={{ width: '100%' }}
          />
        </label>

        <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>
          {t('customerDisplay.bannerImageUrls')}
        </p>
        <p className="muted" style={{ margin: '0 0 0.75rem', fontSize: 13 }}>
          {t('customerDisplay.bannerImageUrlsHint')}
        </p>
        {Array.from({ length: MAX_BANNER_IMAGE_URLS }, (_, index) => (
          <div
            key={index}
            style={{
              marginBottom: '1rem',
              paddingBottom: '1rem',
              borderBottom: index < MAX_BANNER_IMAGE_URLS - 1 ? '1px solid var(--border)' : undefined,
            }}
          >
            <span style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
              {t('customerDisplay.bannerImageUrlN', { n: index + 1 })}
            </span>
            {form.bannerImageUrls[index]?.trim() ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <img
                  src={form.bannerImageUrls[index]}
                  alt=""
                  style={{
                    maxHeight: 72,
                    maxWidth: 160,
                    objectFit: 'contain',
                    borderRadius: 6,
                    border: '1px solid var(--border)',
                    background: 'var(--bg)',
                  }}
                />
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => void clearBanner(index)}
                  disabled={uploadingSlot !== null || saving}
                >
                  {t('customerDisplay.bannerImageClear')}
                </button>
              </div>
            ) : null}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              <label className="btn-secondary" style={{ cursor: uploadingSlot !== null ? 'wait' : 'pointer' }}>
                {uploadingSlot === index
                  ? t('customerDisplay.bannerImageUploading')
                  : t('customerDisplay.bannerImageUpload')}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  hidden
                  disabled={uploadingSlot !== null}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) void uploadBanner(index, file);
                  }}
                />
              </label>
            </div>
            <input
              type="url"
              value={form.bannerImageUrls[index] ?? ''}
              onChange={(e) => {
                const next = bannerUrlsForForm(form.bannerImageUrls);
                next[index] = e.target.value;
                setForm({ ...form, bannerImageUrls: next });
              }}
              placeholder={t('customerDisplay.bannerImageUrlPlaceholder')}
              style={{ width: '100%' }}
            />
          </div>
        ))}
      </div>

      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '1.5rem',
          marginBottom: '1.5rem',
        }}
      >
        <h3 style={{ margin: '0 0 1rem', fontSize: 16 }}>{t('customerDisplay.tipsSection')}</h3>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.25rem' }}>
          <input
            type="checkbox"
            checked={form.tipEnabled}
            onChange={(e) => setForm({ ...form, tipEnabled: e.target.checked })}
          />
          <span style={{ fontWeight: 600 }}>{t('customerDisplay.tipEnabled')}</span>
        </label>

        <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>{t('customerDisplay.percentPresets')}</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
          {numField('tipPreset1Pct', '1', '%')}
          {numField('tipPreset2Pct', '2', '%')}
          {numField('tipPreset3Pct', '3', '%')}
        </div>

        {numField(
          'tipSmallOrderThresholdAmd',
          t('customerDisplay.smallOrderThreshold'),
          t('customerDisplay.smallOrderThresholdHint'),
        )}

        <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>{t('customerDisplay.fixedPresets')}</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
          {numField('tipSmallPreset1Amd', '1', '֏')}
          {numField('tipSmallPreset2Amd', '2', '֏')}
          {numField('tipSmallPreset3Amd', '3', '֏')}
        </div>

        {numField('tipTimeoutSeconds', t('customerDisplay.tipTimeout'), t('customerDisplay.tipTimeoutHint'))}
      </div>

      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '1.5rem',
          marginBottom: '1.5rem',
        }}
      >
        <h3 style={{ margin: '0 0 0.5rem', fontSize: 16 }}>{t('customerDisplay.loyaltySection')}</h3>
        <p className="muted" style={{ margin: '0 0 1.25rem', fontSize: 13 }}>
          {t('customerDisplay.loyaltySectionHint')}
        </p>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.25rem' }}>
          <input
            type="checkbox"
            checked={form.loyaltySignupEnabled}
            disabled={!form.loyaltySignupUrl.trim()}
            onChange={(e) => setForm({ ...form, loyaltySignupEnabled: e.target.checked })}
          />
          <span style={{ fontWeight: 600 }}>{t('customerDisplay.loyaltyEnabled')}</span>
        </label>
        {!form.loyaltySignupUrl.trim() && (
          <p className="muted" style={{ margin: '0 0 1rem', fontSize: 13 }}>
            {t('customerDisplay.loyaltyUrlRequiredHint')}
          </p>
        )}

        <label style={{ display: 'block', marginBottom: '1rem' }}>
          <span style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
            {t('customerDisplay.loyaltySignupUrl')}
          </span>
          <span className="muted" style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>
            {t('customerDisplay.loyaltySignupUrlHint')}
          </span>
          <input
            type="url"
            value={form.loyaltySignupUrl}
            onChange={(e) => {
              const url = e.target.value;
              setForm({
                ...form,
                loyaltySignupUrl: url,
                loyaltySignupEnabled: url.trim() ? form.loyaltySignupEnabled : false,
              });
            }}
            placeholder={t('customerDisplay.joinPlaceholder')}
            style={{ width: '100%' }}
          />
        </label>

        {clientId && joinLinks.length > 0 && (
          <div style={{ marginBottom: '1.25rem' }}>
            <span style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 13 }}>
              {t('customerDisplay.loyaltyQuickLinks')}
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {joinLinks.map((link) => (
                <button
                  key={link.slug}
                  type="button"
                  className="btn-secondary"
                  onClick={() =>
                    setForm({
                      ...form,
                      loyaltySignupUrl: buildLoyaltyJoinUrl(clientId, link.slug),
                    })
                  }
                >
                  {link.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <label style={{ display: 'block', marginBottom: '1rem' }}>
          <span style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
            {t('customerDisplay.loyaltyHeadline')}
          </span>
          <input
            type="text"
            value={form.loyaltySignupHeadline}
            onChange={(e) => setForm({ ...form, loyaltySignupHeadline: e.target.value })}
            placeholder={LOYALTY_SIGNUP_DEFAULTS.headline}
            style={{ width: '100%' }}
          />
        </label>

        <label style={{ display: 'block', marginBottom: '1rem' }}>
          <span style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
            {t('customerDisplay.loyaltySubtext')}
          </span>
          <input
            type="text"
            value={form.loyaltySignupSubtext}
            onChange={(e) => setForm({ ...form, loyaltySignupSubtext: e.target.value })}
            placeholder={LOYALTY_SIGNUP_DEFAULTS.subtext}
            style={{ width: '100%' }}
          />
        </label>

        <label style={{ display: 'block', marginBottom: '0.5rem' }}>
          <span style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
            {t('customerDisplay.loyaltyCtaLabel')}
          </span>
          <input
            type="text"
            value={form.loyaltySignupCtaLabel}
            onChange={(e) => setForm({ ...form, loyaltySignupCtaLabel: e.target.value })}
            placeholder={LOYALTY_SIGNUP_DEFAULTS.ctaLabel}
            style={{ width: '100%' }}
          />
        </label>
      </div>

      <button className="primary" onClick={() => void save()} disabled={saving}>
        {saving ? t('common.loading') : saved ? t('customerDisplay.saved') : t('customerDisplay.save')}
      </button>
    </div>
  );
}
